import type {
    Asset,
    AudioSourceData,
    IconSourceData,
    MediaSourceData,
    OpaqueVideoData,
    Scene,
    TextAsset,
} from '@lumen5/luminary';
import Luminary, { assetNameComponentsFromName, LicenseEnum, VideoData } from '@lumen5/luminary';
import type { Modifications } from '@lumen5/luminary/virtual-designs/core';

import { AgentTaskPipeline } from 'js/src/agent/AgentPipeline';
import type {
    AssetTranscript,
    AudioAssetModel,
    GenerateIconAssetMetadata,
    GenerateMediaAssetMetadata,
    GenerateRecommendedVideoResponse,
    GenerateSceneMetadata,
    GenerateTextAssetMetadata,
    LanguageEnum,
    QueuedTaskResult,
    SceneRecommendation,
    SlideBackgroundModel,
    TranscriptWord,
} from 'js/src/client';
import {
    ApiService,
    MusicRecommendationModeEnum,
    SourceEnum,
    TaskResultMessageTypeEnum,
    WorkspaceMediaModeEnum,
} from 'js/src/client';
import {
    NarrativeThreadEnum,
    RecommendationDefaults,
    TranscriptAssetType,
} from 'js/src/client/types.gen';
import { Toast } from 'js/src/components/common/Toast';
import {
    markSceneGroupAsFinishedLoading,
    markSceneGroupAsLoading,
    SCENE_GROUP_LOADING_TYPES,
    setSkipMediaOptimization,
    setVideoFeedbackFormState,
    shouldSkipAgentPipeline,
} from 'js/src/data/app';
import { startAudioEnhancementOperation } from 'js/src/data/audio-enhancement';
import type { ImmutableAudioAsset } from 'js/src/data/media';
import {
    fetchRecommendedSearchTerms,
    refreshRecommendedAssetsAfterLoadingRecommendedVideo,
    regenerateTranscriptForAsset,
} from 'js/src/data/media';
import { selectMusicRecommendationMode } from 'js/src/data/media-recommendation-preferences';
import {
    setRecommendedVideoResponse,
    setSourceSegmentForRephrasedScene,
} from 'js/src/data/recommendations';
import {
    checkpointVideoAndTranscriptOperation,
    convertTranscriptWordsToMillis,
    getIsOriginalOrMissingTranscript,
    loadNewTranscriptOperation,
} from 'js/src/data/transcript';
import { getLanguageTextDirection, shouldSeparateWordsWithSpaces } from 'js/src/data/translations';
import {
    setBrightnessUserOverride,
    setKenburnUserOverride,
    setTextAlignmentUserOverride,
    setTextPositionUserOverride,
    setTextSizeUserOverride,
} from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { AssetSuffix } from 'js/src/editor/constants';
import { setObjectPropertyValue } from 'js/src/editor/util';
import type { ImmutableMap } from 'js/src/libs/consts';
import {
    COOKIE_KEYS,
    getEnumKeyByEnumValue,
    ImmutableFromJS,
    ImmutableToJS,
} from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import type { AppDispatch } from 'js/src/libs/RootState';
import {
    splitHighlightsAcrossSubscenes,
    VOICEOVER_PLACEHOLDER_ASSET_SOURCE,
} from 'js/src/libs/scene-utils';
import { parseScript } from 'js/src/libs/transcript';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import type { PlaceholderSceneGroupIdPerScriptSegment } from 'js/src/luminary/index';
import {
    addOverlaysFromRecommendationsOperation,
    applyGlobalCaptionStylesToSceneGroupOperation,
    buildVoiceoverSceneOperation,
    canVoiceDrivenBaseAssetBeOverwritten,
    clearSourceData,
    deleteAllOverlaysFromSceneGroupOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
    fetchRecommendedVoiceDrivenVideoOperation,
    getEditableMediaAssets,
    getEditableSceneTextAssetArray,
    isAIVoiceoverSceneGroup,
    recommendedMediaSourceData,
    saveRawLuminary,
    selectOverlayOperation,
    setInitialVolumeForMediaAsset,
    waitUntilTemplateBundleLoaded,
} from 'js/src/luminary/index';
import { type OperationCall } from 'js/src/luminary/luminary-hooks';
import { setAgentPipeline } from 'js/src/luminary/luminary-operations';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import {
    muteOutroMusicIfHasMediaWithVolume,
    resizeTextToFitOnSceneForEntireVideo,
} from 'js/src/luminary/luminary-utils';
import { isErrorObject, OperationAbortedError } from 'js/src/types';
import Cookies from 'js-cookie';

export const PLACEHOLDER_VOICEOVER_MODEL_DATA: AudioAssetModel = {
    mp3_url:
        'https://storage.googleapis.com/lumen5-user-070c1e27-d23e-4f58-8fbd-95f6caf8bc94/outputmp3eQoX5Z.normalized.mp3',
    orig_mp3_url:
        'https://storage.googleapis.com/lumen5-user-070c1e27-d23e-4f58-8fbd-95f6caf8bc94/outputmp3eQoX5Z.normalized.mp3',
    duration: 597, // the database holds audio duration in seconds :(
    source: SourceEnum.SOURCE_USER_VOICEOVER,
    source_id: 'lumen5-user-070c1e27-d23e-4f58-8fbd-95f6caf8bc94/outputmp3eQoX5Z.normalized.mp3',
    license: LicenseEnum.USER_PRIVATE,
    attribution: '',
    description: '',
    tags: [],
    title: 'output',
    name: 'output',
    quality: 0,
    uuid: '2974fb1e-10be-4cd3-91ef-957f16575401',
    id: 2879975,
    history_index_user_ids: [],
    has_voiceover: true,
    waveform: [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        0, 0, 0, 0, 0, 0, 0,
    ],
    captions: null,
    ai_voice: null,
    ai_voice_speed: null,
};

export type SetAndApplyVoiceoverParams = {
    sceneGroupId: string;
    audioAssetData: ImmutableMap<AudioAssetModel> | null;
    avatarAssetData: ImmutableMap<SlideBackgroundModel> | null;
    avatarBackgroundAssetData?: MediaSourceData | null;
    captionsLanguage: string | null;
    forceCaptionGeneration: boolean;
    selectedBlueprintId: number | null;
    generateRecommendedVideoResponse: {
        data: QueuedTaskResult;
        request: Request;
        response: Response;
    } | null;
    sceneRecommendations: SceneRecommendation[] | null;
    isGlobalVideo: boolean;
    transcript?: AssetTranscript | null;
    recommendOverlays: boolean;
    systemTriggered: boolean;
    parentActionGroup: string;
};

export type LuminaryOperationsParams = {
    luminary: Luminary;
    dispatch: AppDispatch;
    getState: GetRootState;
    call: OperationCall;
};
export type SetAnApplyVoiceoverOperationParams = LuminaryOperationsParams &
    SetAndApplyVoiceoverParams;

export const addScenesFromRecommendations = (
    recommendations: Array<SceneRecommendation>,
    luminary: Luminary,
    dispatch: AppDispatch,
    placeholderSceneGroupIdPerScriptSegment: PlaceholderSceneGroupIdPerScriptSegment,
    sceneDesignId: string,
    creationMethod: string,
    call: OperationCall,
    optimizing: boolean,
    is_global_video: boolean = true,
    systemTriggered: boolean,
    parentActionGroup: string,
): string[] => {
    /**
     * if is_global_video that means we're adding scenes to the global luminary instance. in that case we want to
     * track analytics and do follow-on actions, so dispatch and call are required
     * If not that means we're generating a background recommended video so those actions are not desired
     *
     * NOTE: placeholderSceneGroupIdPerScriptSegment is a map that keeps track of the placeholder scene groups. This
     *       method edits the map in place.
     */
    ErrorReporter.addBreadcrumb({
        category: 'recommendation',
        message: 'Adding scenes from recommendations',
        level: 'info',
        data: {
            recommendations,
            sceneDesignId,
            creationMethod,
            is_global_video,
        },
    });

    // Track newly created scene group IDs to return to caller
    const newlyCreatedSceneGroupIds: string[] = [];

    // Update placeholder scene count based on the segments retrieved in the response
    const numPlaceholders = Object.keys(placeholderSceneGroupIdPerScriptSegment).length;
    const totalScenes = recommendations.length;
    if (totalScenes > numPlaceholders) {
        for (let i = numPlaceholders; i < totalScenes; i++) {
            const { sceneGroup } = luminary.addSceneGroup({ sceneDesignId });
            placeholderSceneGroupIdPerScriptSegment[i] = sceneGroup.id;
            newlyCreatedSceneGroupIds.push(sceneGroup.id);

            if (is_global_video && dispatch) {
                dispatch(
                    markSceneGroupAsLoading({
                        sceneGroupId: sceneGroup.id,
                        type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE,
                        context: {
                            loadingMsg: '',
                        },
                    }),
                );
            }
        }
    } else if (totalScenes < numPlaceholders) {
        for (let i = numPlaceholders - 1; i >= totalScenes; i--) {
            const placeholderSceneGroupId = placeholderSceneGroupIdPerScriptSegment[i];
            if (placeholderSceneGroupId) {
                try {
                    luminary.removeSceneGroup(placeholderSceneGroupId);
                } catch (error) {
                    /* do nothing */
                }
                delete placeholderSceneGroupIdPerScriptSegment[i];
            }
        }
    }

    // for each recommended scene, populate the scene!
    luminary.batchRun(() => {
        recommendations.forEach((data, sceneIndex) => {
            if (!data) {
                /*
                Imagine the backend knows that it is going to recommend 4 scenes.
                A partial scene recommendation could be in the form:
                    [
                        {recommendation for scene 1},
                        {recommendation for scene 2},
                        null,  // this scene's recommendations aren't ready yet!
                        null,  // neither are this one's
                    ]
                */
                return;
            }

            // check if we've already populated the scene for this recommendation, if so, skip it
            const placeholderSceneGroupId = placeholderSceneGroupIdPerScriptSegment[sceneIndex];
            if (placeholderSceneGroupId === null) {
                return;
            }

            // delete placeholder scene and create a new one with the recommended design
            const sceneGroup = luminary.getSceneGroupById(placeholderSceneGroupId);
            if (!sceneGroup) {
                ErrorReporter.capture(
                    `Could not find placeholder scene group with id ${placeholderSceneGroupId}`,
                );
                return;
            }
            sceneGroup.removeScene(sceneGroup.scenes[0].id);
            const sceneDesignId = data.scene_data.scene_design;
            const scene = sceneGroup.addScene({ sceneDesignId });
            ensureOnlyOneOutroScene(scene, luminary);

            const isOverlay = false; // method only used for text-on-media currently
            populateSceneFromCreationRecommendations({
                luminary,
                sceneId: scene.id,
                strategy: ImmutableFromJS(data.scene_data),
                isOverlay,
                isGlobalVideo: is_global_video,
                dispatch,
                call,
            });

            // clear the placeholder scene group
            placeholderSceneGroupIdPerScriptSegment[sceneIndex] = null;

            void call(fetchAndSetRecommendedSceneAttributesOperation, {
                sceneId: scene.id,
                systemTriggered,
                parentActionGroup,
            });

            if (is_global_video) {
                populateSourceSegmentForRephrasedScene(scene.id, data)(dispatch);

                // must call analytics AFTER populating the recommendations for everything to be accurate
                const reccMediaData = recommendedMediaSourceData(data.scene_data);
                analytics.track('New Scene Added', {
                    method: creationMethod,
                    sceneId: scene.id,
                    sceneGroupId: sceneGroup.id,
                    sceneDesignId: scene.sceneDesign,
                    sceneCategory: scene.sceneCategory,
                    creationDisplayGroupName: data.scene_data.display_group_name ?? 'unknown',
                    templateId: luminary.template.id,
                    mediaType: reccMediaData?.type ?? 'none',
                    recommendedMediaSource: reccMediaData.sourceData
                        ? getEnumKeyByEnumValue(SourceEnum, reccMediaData.sourceData.source)
                        : null,
                    isVirtualDesign:
                        data.scene_data.modifications &&
                        Object.keys(data.scene_data.modifications).length > 0,
                });

                // mark the scene group as finished loading
                if (!optimizing) {
                    dispatch(
                        markSceneGroupAsFinishedLoading({
                            sceneGroupId: sceneGroup.id,
                        }),
                    );
                }

                // every time we update a scene, make sure the undo-redo stack is updated as well.
                // The user could be doing other things while the scenes are being updated, so we want to make sure
                // that the undo-redo stack is constantly updated.
                dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(luminary),
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
            }
        });
    });

    recommendations.forEach((data, sceneIndex) => {
        const sceneGroupId = placeholderSceneGroupIdPerScriptSegment[sceneIndex];
        if (sceneGroupId && !optimizing) {
            dispatch(
                markSceneGroupAsFinishedLoading({
                    sceneGroupId,
                }),
            );
        }
    });

    // can't resize text in `batchRun()` since it relies on the asset values being updated
    resizeTextToFitOnSceneForEntireVideo(luminary);

    // ensure outro has no music if it has media with volume
    muteOutroMusicIfHasMediaWithVolume(luminary);

    return newlyCreatedSceneGroupIds;
};

const applyColorGraphicsMap = (asset: Asset, colorGraphicsMap: Record<string, number>) => {
    const colorGraphicsMapKeys = Object.keys(colorGraphicsMap);
    for (const key of colorGraphicsMapKeys) {
        setObjectPropertyValue(asset, key, colorGraphicsMap[key]);
    }
};

export const ensureOnlyOneOutroScene = (newlyAddedScene: Scene, luminary: Luminary) => {
    // safety check: if we're creating a new scene we need to make sure we didn't already
    // have an outro scene! if so we'll run into a case with >1 outro scenes which is not
    // expected by the system
    // this code checks for this case and fixes if needed
    if (newlyAddedScene.sceneCategory !== VideoData.CATEGORY_OUTRO) {
        return;
    }

    // check if we have >1 outro scenes
    const numOutroScenes = luminary.sceneGroups.reduce((count, sceneGroup) => {
        sceneGroup.scenes.forEach((scene) => {
            if (scene.sceneCategory === VideoData.CATEGORY_OUTRO) {
                count++;
            }
        });
        return count;
    }, 0);
    if (numOutroScenes <= 1) {
        return;
    }

    // whoops, we just added an outro to a video that already had an outro!
    // delete any outro scene groups other than the one we just added
    const newlyAddedSceneGroup = newlyAddedScene.sceneGroup;
    const sceneGroupIdsToDelete: string[] = [];
    luminary.sceneGroups.forEach((sceneGroup) => {
        if (sceneGroup.id === newlyAddedSceneGroup.id) {
            return;
        }
        if (sceneGroup.scenes.some((scene) => scene.sceneCategory === VideoData.CATEGORY_OUTRO)) {
            sceneGroupIdsToDelete.push(sceneGroup.id);
        }
    });
    sceneGroupIdsToDelete.forEach((sceneGroupId) => {
        luminary.removeSceneGroup(sceneGroupId);
    });
};

/*
 * This method applies scene creation recommendations to the scene. This means it sets text/icon/media assets to the
 * values recommended by the server.
 */
export const populateSceneFromCreationRecommendations = ({
    luminary,
    sceneId,
    strategy,
    isOverlay,
    isGlobalVideo,
    dispatch,
    call,
}: {
    luminary: Luminary;
    sceneId: string;
    strategy: ImmutableMap<GenerateSceneMetadata>;
    isOverlay: boolean;
    isGlobalVideo: boolean;
    dispatch: AppDispatch;
    call: OperationCall;
}) => {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }

    // Handle modifications (aka virtual scene designs)
    if (strategy.get('modifications')) {
        const modifications = strategy.get('modifications').toJS() as Record<string, Modifications>;

        // do the scene-level modifications first
        if (modifications[VideoData.SCENE_MODIFICATION_KEY]) {
            const key = VideoData.SCENE_MODIFICATION_KEY;
            try {
                if (modifications[key].textPositionPreset) {
                    scene.applyTextPreset(modifications[key].textPositionPreset);
                    isGlobalVideo &&
                        dispatch(
                            setTextPositionUserOverride({
                                sceneId: scene.id,
                                isAutomatic: false,
                                systemTriggered: true,
                                parentActionGroup: undefined,
                            }),
                        );
                }
                if (modifications[key].watermarkPosition) {
                    scene.watermark.position = modifications[key].watermarkPosition;
                }
                if (modifications[key].watermarkIndex !== undefined) {
                    const watermarkIndex = modifications[key].watermarkIndex;
                    const numWatermarks = luminary.template.watermarkSources.length;
                    if (watermarkIndex >= 0 && watermarkIndex < numWatermarks) {
                        scene.watermark.sourceData =
                            VideoData.templateWatermarkSourcesByIndex(watermarkIndex);
                    } else {
                        ErrorReporter.capture(
                            new Error(
                                `Invalid watermarkIndex ${watermarkIndex} for scene ${sceneId}, template has ${numWatermarks} watermarks`,
                            ),
                        );
                    }
                }
                if (modifications[key].displayCaptions) {
                    scene.displayCaptions = modifications[key].displayCaptions;
                }
            } catch (e) {
                ErrorReporter.addBreadcrumb({
                    category: 'recommendation',
                    message: `Scene ${sceneId} not found`,
                    data: { sceneId, modifications },
                });
                ErrorReporter.capture(e);
            }
        }

        // update each asset with modifications
        Object.keys(modifications).forEach((assetName) => {
            if (assetName === VideoData.SCENE_MODIFICATION_KEY) {
                // already handled above
                return;
            }

            try {
                const asset = scene.getAssetByName(assetName);
                if (asset === undefined) {
                    if (
                        assetName.endsWith(AssetSuffix.DisableSwap) ||
                        assetName.endsWith(AssetSuffix.EmbeddedLogo)
                    ) {
                        return;
                    }
                    ErrorReporter.capture(
                        `Skipping unknown asset "${assetName}" when applying virtual design modifications`,
                        { level: 'warning' },
                    );
                    return;
                }

                // text properties
                if (VideoData.isTextAsset(asset)) {
                    if (modifications[assetName].textSize) {
                        asset.textSize = modifications[assetName].textSize;
                        isGlobalVideo &&
                            dispatch(
                                setTextSizeUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].fontUrl) {
                        asset.normalStyle.fontUrl = modifications[assetName].fontUrl;
                    }
                    if (modifications[assetName].highlightFontUrl) {
                        asset.highlightStyle.fontUrl = modifications[assetName].highlightFontUrl;
                    }
                    if (modifications[assetName].textAlign) {
                        asset.textAlign = modifications[assetName].textAlign;
                        isGlobalVideo &&
                            dispatch(
                                setTextAlignmentUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].normalFillColor) {
                        asset.normalStyle.fill = modifications[assetName].normalFillColor;
                    }
                    if (modifications[assetName].highlightFillColor) {
                        asset.highlightStyle.fill = modifications[assetName].highlightFillColor;
                    }
                    if (modifications[assetName].colorGraphicsMap) {
                        applyColorGraphicsMap(asset, modifications[assetName].colorGraphicsMap);
                    }
                    if (modifications[assetName].transformation) {
                        const {
                            position: { x: posX, y: posY },
                            anchor: { x: anchX, y: anchY },
                        } = modifications[assetName].transformation;
                        dispatch(
                            setTextPositionUserOverride({
                                sceneId: scene.id,
                                isAutomatic: false,
                                systemTriggered: true,
                                parentActionGroup: undefined,
                            }),
                        );
                        asset.transformation.anchor.x = anchX;
                        asset.transformation.anchor.y = anchY;
                        asset.transformation.position.x = posX;
                        asset.transformation.position.y = posY;
                    }
                    if (modifications[assetName].hasUserChangedMaxWidth) {
                        asset.hasUserChangedMaxWidth = true;
                    }
                    if (modifications[assetName].maxHeight) {
                        asset.maxHeight = modifications[assetName].maxHeight;
                    }
                    if (modifications[assetName].maxWidth) {
                        asset.maxWidth = modifications[assetName].maxWidth;
                    }
                    return;
                }
                // animated captions properties
                if (VideoData.isFancyCaptionAsset(asset)) {
                    if (modifications[assetName].textSize) {
                        asset.textSize = modifications[assetName].textSize;
                        isGlobalVideo &&
                            dispatch(
                                setTextSizeUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].fontUrl) {
                        asset.normalStyle.fontUrl = modifications[assetName].fontUrl;
                    }
                    if (modifications[assetName].textAlign) {
                        asset.textAlign = modifications[assetName].textAlign;
                        isGlobalVideo &&
                            dispatch(
                                setTextAlignmentUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].normalFillColor) {
                        asset.normalStyle.fill = modifications[assetName].normalFillColor;
                    }
                    if (modifications[assetName].colorGraphicsMap) {
                        applyColorGraphicsMap(asset, modifications[assetName].colorGraphicsMap);
                    }
                    if (modifications[assetName].transformation) {
                        const {
                            position: { x: posX, y: posY },
                            anchor: { x: anchX, y: anchY },
                        } = modifications[assetName].transformation;
                        // set text preset null somehow..?
                        dispatch(
                            setTextPositionUserOverride({
                                sceneId: scene.id,
                                isAutomatic: false,
                                systemTriggered: true,
                                parentActionGroup: undefined,
                            }),
                        );
                        asset.transformation.anchor.x = anchX;
                        asset.transformation.anchor.y = anchY;
                        asset.transformation.position.x = posX;
                        asset.transformation.position.y = posY;
                    }
                    if (modifications[assetName].hasUserChangedMaxWidth) {
                        asset.hasUserChangedMaxWidth = true;
                    }
                    if (modifications[assetName].maxHeight) {
                        asset.maxHeight = modifications[assetName].maxHeight;
                    }
                    if (modifications[assetName].maxWidth) {
                        asset.maxWidth = modifications[assetName].maxWidth;
                    }
                    return;
                }
                // asset group properties
                if (VideoData.isAssetGroup(asset)) {
                    if (modifications[assetName].alignChildren) {
                        asset.alignChildren = modifications[assetName].alignChildren;
                        isGlobalVideo &&
                            dispatch(
                                setTextAlignmentUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].colorGraphicsMap) {
                        applyColorGraphicsMap(asset, modifications[assetName].colorGraphicsMap);
                    }
                    if (modifications[assetName].transformation) {
                        const {
                            position: { x: posX, y: posY },
                            anchor: { x: anchX, y: anchY },
                        } = modifications[assetName].transformation;
                        // set text preset null somehow..?
                        dispatch(
                            setTextPositionUserOverride({
                                sceneId: scene.id,
                                isAutomatic: false,
                                systemTriggered: true,
                                parentActionGroup: undefined,
                            }),
                        );
                        asset.transformation.anchor.x = anchX;
                        asset.transformation.anchor.y = anchY;
                        asset.transformation.position.x = posX;
                        asset.transformation.position.y = posY;
                    }
                    if (modifications[assetName].hasUserChangedMaxWidth) {
                        asset.hasUserChangedMaxWidth = true;
                    }
                    if (modifications[assetName].maxHeight) {
                        asset.maxHeight = modifications[assetName].maxHeight;
                    }
                    return;
                }
                // media properties
                if (VideoData.isMediaAsset(asset)) {
                    if (modifications[assetName].brightness) {
                        asset.brightness = modifications[assetName].brightness;
                        isGlobalVideo &&
                            dispatch(
                                setBrightnessUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].backgroundAnimation) {
                        asset.kenburn = modifications[assetName].backgroundAnimation;
                        isGlobalVideo &&
                            dispatch(
                                setKenburnUserOverride({
                                    sceneId: scene.id,
                                    isAutomatic: false,
                                    systemTriggered: true,
                                    parentActionGroup: undefined,
                                }),
                            );
                    }
                    if (modifications[assetName].contrast) {
                        asset.contrast = modifications[assetName].contrast;
                    }
                    if (modifications[assetName].hue) {
                        asset.hue = modifications[assetName].hue;
                    }
                    if (modifications[assetName].saturation) {
                        asset.saturation = modifications[assetName].saturation;
                    }
                    if (modifications[assetName].lightness) {
                        asset.lightness = modifications[assetName].lightness;
                    }
                    if (modifications[assetName].blur) {
                        asset.blur = modifications[assetName].blur;
                    }
                    if (modifications[assetName].vignetting) {
                        asset.vignetting = modifications[assetName].vignetting;
                    }
                    if (modifications[assetName].noise) {
                        asset.noise = modifications[assetName].noise;
                    }
                    if (modifications[assetName].grayscale) {
                        asset.enableGreyscale = modifications[assetName].grayscale;
                    }
                    if (modifications[assetName].playbackRate !== undefined && asset.isVideo) {
                        asset.playbackRate = modifications[assetName].playbackRate;
                    }
                    return;
                }
                // color asset
                if (VideoData.isColorAsset(asset)) {
                    if (modifications[assetName].sourceColor) {
                        asset.sourceColor = modifications[assetName].sourceColor;
                    }
                    return;
                }
                // circle & rectangle assets
                if (VideoData.isCircleAsset(asset) || VideoData.isRectangleAsset(asset)) {
                    if (modifications[assetName].color) {
                        asset.color = modifications[assetName].color;
                    }
                    if (modifications[assetName].lineColor) {
                        asset.lineColor = modifications[assetName].lineColor;
                    }
                    if (modifications[assetName].colorGraphicsMap) {
                        applyColorGraphicsMap(asset, modifications[assetName].colorGraphicsMap);
                    }
                    return;
                }
                // icon/lottie assets
                if (VideoData.isIconAsset(asset) || VideoData.isLottieAsset(asset)) {
                    if (modifications[assetName].colorGraphicsMap) {
                        applyColorGraphicsMap(asset, modifications[assetName].colorGraphicsMap);
                    }
                    if (modifications[assetName].colorMaps) {
                        modifications[assetName].colorMaps.forEach((color, index) => {
                            if (asset.colorMaps[index] !== undefined) {
                                asset.colorMaps[index].targetColor = color;
                            }
                        });
                    }
                    return;
                }
                // gradient assets
                if (VideoData.isGradientAsset(asset)) {
                    if (modifications[assetName].colorGraphicsMap) {
                        applyColorGraphicsMap(asset, modifications[assetName].colorGraphicsMap);
                    }
                    if (modifications[assetName].colorStops) {
                        modifications[assetName].colorStops.forEach((color, index) => {
                            asset.colorStops[index].color = color;
                        });
                    }
                    return;
                }
                throw new Error(`Unknown asset type: ${asset.classType}`);
            } catch (e) {
                ErrorReporter.addBreadcrumb({
                    category: 'recommendation',
                    message: `Asset ${assetName} not found`,
                    data: { assetName, sceneId, modifications },
                });
                ErrorReporter.capture(e);
            }
        });
    }

    // Update the assets on scene.
    const assets = strategy.get('assets');
    const textAssetNamesUpdated: string[] = [];
    try {
        assets.map((strategyAsset) => {
            const asset = scene.getAssetByName(strategyAsset.get('name'));
            if (asset) {
                if (VideoData.isTextAsset(asset)) {
                    asset.text = (strategyAsset as ImmutableMap<GenerateTextAssetMetadata>).get(
                        'text',
                    );
                    asset.textHighlights = ImmutableToJS(
                        (strategyAsset as ImmutableMap<GenerateTextAssetMetadata>).get(
                            'highlights',
                        ),
                    );
                    textAssetNamesUpdated.push(asset.name);
                } else if (VideoData.isMediaAsset(asset)) {
                    // assign sourceData
                    asset.sourceData = (strategyAsset as ImmutableMap<GenerateMediaAssetMetadata>)
                        .get('sourceData')
                        .toJS() as MediaSourceData;
                    // set the attribution
                    scene.attribution = (strategyAsset as ImmutableMap<GenerateMediaAssetMetadata>)
                        .get('sourceData')
                        .get('attribution') as string | null;
                    // set the volume levels
                    setInitialVolumeForMediaAsset(luminary, asset, scene, isOverlay);
                } else if (VideoData.isIconAsset(asset)) {
                    // assign sourceData
                    asset.replace(
                        (strategyAsset as ImmutableMap<GenerateIconAssetMetadata>)
                            .get('sourceData')
                            .toJS() as IconSourceData,
                    );
                }
            }
        });
    } catch (e) {
        ErrorReporter.setContext('video recommendation data', {
            sceneId,
            strategy: ImmutableToJS(strategy),
        });
        ErrorReporter.capture(e);
    }
    // it's possible that there are some editable text assets with placeholder content that weren't specified
    // by the strategy – clear them!
    scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((asset) => asset.isEditable && !textAssetNamesUpdated.includes(asset.name))
        // don't clear suffixed assets (they're special)
        .filter((asset) => assetNameComponentsFromName(asset.name).suffix === '')
        .forEach((asset) => {
            asset.text = '';
            asset.textHighlights = [];
        });

    // Handle subscene recommendations
    const textAssets = scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((asset) => asset.isEditable)
        .filter((asset) => assetNameComponentsFromName(asset.name).suffix === '');
    // when we encounter text with subscenes, we need to split the text across the subscenes
    // we store these subscene texts (chunks) in a map
    let numSubscenes = 1;
    const chunkMap: { [k: string]: string[] } = {};
    for (const asset of textAssets) {
        const chunks = asset.text.split(RecommendationDefaults.SUBSCENE_TOKEN) ?? [''];
        chunkMap[asset.name] = chunks;
        if (chunks.length > numSubscenes) {
            numSubscenes = chunks.length;
        }
    }
    // normalize number of subscenes for each asset
    for (const asset of textAssets) {
        const chunks = chunkMap[asset.name];
        while (chunks.length < numSubscenes) {
            chunks.push('');
        }
    }
    // actually create subscenes
    for (let i = 1; i < numSubscenes; i++) {
        scene.addSubscene(i);
    }
    // set text for each subscene backwards
    for (let i = numSubscenes - 1; i >= 0; i--) {
        for (const asset of textAssets) {
            const subsceneAsset = scene.subscenes[0].getAssetByName(asset.name);
            if (!subsceneAsset) {
                // the asset in question does not exist on the subscene
                continue;
            }
            const chunks = chunkMap[asset.name];
            const highlightChunks = splitHighlightsAcrossSubscenes(asset.textHighlights, chunks);
            const subscene = scene.subscenes[i];
            try {
                (subscene.getAssetByName(asset.name) as TextAsset).text = chunks[i];
                (subscene.getAssetByName(asset.name) as TextAsset).textHighlights =
                    highlightChunks[i];
            } catch (e) {
                ErrorReporter.capture(e);
            }
        }
    }
};

export const populateSourceSegmentForRephrasedScene = (
    sceneId: string,
    sceneRecommendation: SceneRecommendation,
) => {
    return (dispatch: AppDispatch) => {
        const segment = sceneRecommendation.scene_data.segment;
        const textSet = sceneRecommendation.scene_data.assets
            .filter((asset) => asset.type === VideoData.TEXT_ASSET_TYPE)
            .map((asset) => (asset as GenerateTextAssetMetadata).text);
        const nonEmptyTextSet = textSet.filter((text) => text.trim() !== '');
        if (nonEmptyTextSet.length !== 1 || nonEmptyTextSet[0] !== segment) {
            dispatch(
                setSourceSegmentForRephrasedScene({
                    sceneId,
                    sourceSegment: segment,
                }),
            );
        }
    };
};

export const applyTemplateStyleFromVideoData = async (
    luminary: Luminary,
    baseVideoData: OpaqueVideoData,
) => {
    const dataLuminary = new Luminary({
        fetchResources: false,
        disableRenderer: true,
        autoDraw: false,
        autoUpdate: false,
    });
    dataLuminary.load(baseVideoData);
    const template_data = dataLuminary.template.save();
    await dataLuminary.destroy();

    // set template settings
    luminary.template.load(template_data);
};

/**
 * Function to set and apply a voiceover asset to a voiceover scene group. The scene group must already have
 * added a voiceover asset. This function will not run if the scene group isn't a voiceover scene group.
 * @param sceneGroupId - voiceover scene group id
 * @param audioAssetData - Immutable Map of voiceover audio asset data. Leave undefined, if you want to use the existing asset
 * @param avatarAssetData - Immutable Map of voiceover audio asset data. Leave undefined, if you want to use the existing asset
 * @param captionsLanguage - specified captions language (undefined = auto)
 * @param forceCaptionGeneration - set to true if you want the captions to always be generated
 * @param systemTriggered boolean
 * @param parentActionGroup uuid
 * @returns void
 */
export const setAndApplyVoiceoverAssetForSceneGroup = async ({
    sceneGroupId,
    audioAssetData = null,
    avatarAssetData = null,
    avatarBackgroundAssetData = null,
    captionsLanguage = null, // specified captions language (null = auto)
    forceCaptionGeneration = false, // set to true if you want the captions to always be generated
    selectedBlueprintId = null,
    generateRecommendedVideoResponse,
    sceneRecommendations,
    isGlobalVideo = true,
    transcript = null,
    recommendOverlays = true,
    luminary,
    dispatch,
    getState,
    call,
    systemTriggered = false,
    parentActionGroup = uuid(),
}: SetAnApplyVoiceoverOperationParams) => {
    dispatch(setSkipMediaOptimization({ skip: false }));

    const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
    if (!sceneGroup) {
        return false;
    }

    if (!sceneGroup.voiceoverAsset) {
        Toast.error(
            'To use this voiceover, drag this file to an empty voiceover scene.',
            'Voiceovers are only supported on voiceover scenes',
        );
        return false;
    }

    if (audioAssetData !== null && !audioAssetData.get('has_voiceover')) {
        Toast.info('This audio track does not contain voiceover.');
        return false;
    }

    const overwritingExistingAsset =
        !!audioAssetData && // we are not overwriting if we didn't provide a new asset
        !!sceneGroup.voiceoverAsset.sourceData && // we are not overwriting if there is no existing asset
        sceneGroup.voiceoverAsset.sourceData.uuid !== VOICEOVER_PLACEHOLDER_ASSET_SOURCE.uuid && // we are not overwriting if the existing asset is the placeholder
        sceneGroup.voiceoverAsset.sourceData.uuid !== audioAssetData.get('uuid'); // we are not overwriting if the existing asset is the same as the new asset
    let audioAssetSourceData: ImmutableMap<AudioSourceData>;
    if (!audioAssetData) {
        audioAssetSourceData = ImmutableFromJS(sceneGroup.voiceoverAsset.sourceData);
    } else {
        audioAssetSourceData = audioAssetData as ImmutableMap<AudioSourceData>; // convert the AudioAssetModel db object to source data object
    }

    if (overwritingExistingAsset) {
        const isOriginalTranscript = getIsOriginalOrMissingTranscript(getState(), sceneGroup.id);
        const canOverwriteBaseAsset = canVoiceDrivenBaseAssetBeOverwritten(
            sceneGroup,
            isOriginalTranscript,
            audioAssetData.toJS(),
        );
        if (!canOverwriteBaseAsset.result) {
            if (canOverwriteBaseAsset.reason) {
                Toast.error(canOverwriteBaseAsset.reason, canOverwriteBaseAsset.title || '');
            }
            return false;
        }
    }

    const startTime = performance.now();
    const scene = sceneGroup.scenes[0];
    const sceneId = scene.id;
    let audioAssetId;
    let transcriptWords;
    let language;

    if (isGlobalVideo) {
        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId,
                type: SCENE_GROUP_LOADING_TYPES.RECOMMENDING_OVERLAYS,
            }),
        );
    }

    try {
        audioAssetId = audioAssetSourceData.get('uuid');
        if (!audioAssetId) throw new Error('Audio asset id is missing');
        if (forceCaptionGeneration) {
            await regenerateTranscriptForAsset(
                audioAssetId,
                TranscriptAssetType.AUDIO,
                captionsLanguage ?? undefined,
            );
        }
        if (!transcript) {
            const response = await ApiService.assetTranscriptRetrieve({
                path: { asset_id: audioAssetId },
                query: { asset_type: TranscriptAssetType.AUDIO },
            });
            transcript = response.data;
        }
        transcriptWords = convertTranscriptWordsToMillis(transcript.transcriptWords);
        language = transcript.language;
        if (!language) throw new Error('Language is missing');

        await call(buildVoiceoverSceneOperation, {
            sceneGroupId: sceneGroup.id,
            audioAssetData: audioAssetSourceData,
            avatarAssetData,
            avatarBackgroundAssetData,
            blueprintId: selectedBlueprintId,
        });

        // Create and load transcript editor instance to global context
        await call(
            loadNewTranscriptOperation,
            transcriptWords,
            language,
            sceneGroupId,
            sceneId,
            true,
            false,
            startTime,
            false,
            systemTriggered,
            parentActionGroup,
        );

        // Copy caption styles (scene design ID and granular overrides) from existing
        // voice-driven scene groups to maintain caption style consistency
        call(applyGlobalCaptionStylesToSceneGroupOperation, sceneGroup);

        if (isGlobalVideo) {
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }

        if (recommendOverlays) {
            // Apply transcript to scene group in order to update captions
            if (sceneRecommendations) {
                const separateWordsWithSpaces = shouldSeparateWordsWithSpaces(getState(), language);
                const textDirection = getLanguageTextDirection(getState(), language);
                const transcriptData = {
                    language,
                    textDirection: textDirection,
                    words: transcriptWords,
                    separateWordsWithSpaces,
                };
                sceneGroup.originalTranscript.transcriptData = transcriptData;
                sceneGroup.captions.transcript.transcriptData = transcriptData;
            }

            if (!generateRecommendedVideoResponse && !sceneRecommendations) {
                const words = transcriptWords.map((word) => word.word);
                generateRecommendedVideoResponse = await call(
                    fetchRecommendedVoiceDrivenVideoOperation,
                    {
                        words,
                        language,
                        narrative_thread: avatarAssetData
                            ? NarrativeThreadEnum.TALKING_HEAD
                            : NarrativeThreadEnum.VOICEOVER,
                        selectedBlueprintId,
                    },
                );
            }

            await setRecommendedVoiceDrivenVideo(
                sceneGroupId,
                avatarAssetData ? NarrativeThreadEnum.TALKING_HEAD : NarrativeThreadEnum.VOICEOVER,
                selectedBlueprintId,
                generateRecommendedVideoResponse,
                sceneRecommendations,
                isGlobalVideo,
                luminary,
                dispatch,
                call,
                true,
                getState,
                systemTriggered,
                parentActionGroup,
            );
        } else if (isGlobalVideo) {
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
        }
    } catch (e) {
        Toast.error('Sorry, there was a problem generating the transcript.');
        dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
        ErrorReporter.capture(e);
        return false;
    }

    // Turn audio enhancement on by default unless it is an AI voiceover or user is on free plan
    // re-grab the scene group from luminary. We've just done some async stuff, and the user could have deleted it in the meantime.
    if (isGlobalVideo && SERVER.USER.plan_price > 0) {
        const sceneGroupAfterAwaiting = luminary.getSceneGroupById(sceneGroupId);
        if (sceneGroupAfterAwaiting && !isAIVoiceoverSceneGroup(sceneGroupAfterAwaiting)) {
            void call(startAudioEnhancementOperation, sceneGroupAfterAwaiting.id);
        }
    }

    return true;
};

/**
 * This method is used to create a recommended "voiceover video".
 */
export const setRecommendedVoiceDrivenVideo = async (
    sceneGroupId: string,
    narrative_thread: NarrativeThreadEnum,
    selectedBlueprintId: number | null,
    generateRecommendedVideoTask: {
        data: QueuedTaskResult;
        request: Request;
        response: Response;
    } | null,
    sceneRecommendations: SceneRecommendation[] | null,
    isGlobalVideo: boolean,
    luminary: Luminary,
    dispatch: AppDispatch,
    call: OperationCall,
    optimizing: boolean,
    getState: GetRootState,
    systemTriggered = false,
    parentActionGroup = uuid(),
) => {
    const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
    if (!sceneGroup) {
        return;
    }

    if (isGlobalVideo) {
        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId,
                type: SCENE_GROUP_LOADING_TYPES.RECOMMENDING_OVERLAYS,
            }),
        );
    }

    await waitUntilTemplateBundleLoaded(luminary);

    // If no music has been set, recommend music
    if (isGlobalVideo && luminary.music === null) {
        const musicRecommendationMode = selectMusicRecommendationMode(getState());
        void fetchAndSetRecommendMusic(luminary, musicRecommendationMode);
    }

    if (isGlobalVideo) {
        // if there are existing overlays, remove them
        call(
            deleteAllOverlaysFromSceneGroupOperation,
            sceneGroupId,
            systemTriggered,
            parentActionGroup,
        );
    }

    try {
        if (sceneRecommendations) {
            await call(
                addOverlaysFromRecommendationsOperation,
                {
                    scenes: sceneRecommendations,
                    media_candidates: {
                        source_media: [],
                        stock_media: [],
                        stock_icons: [],
                        uploaded_icons: [],
                        uploaded_media: [],
                        stock_media_by_query: [],
                    },
                    media_options: {
                        source_media_enabled: true,
                        stock_media_enabled: true,
                        stock_icons_enabled: true,
                        media_guidance: '',
                        dam_media_enabled: false,
                        workspace_media_mode: WorkspaceMediaModeEnum.OFF,
                        workspace_folder_ids: [],
                        minimum_stock_suitability_score: null,
                    },
                    script: '',
                },
                sceneGroupId,
                isGlobalVideo,
                systemTriggered,
                parentActionGroup,
            );
        } else {
            if (!generateRecommendedVideoTask)
                throw new Error('Missing generateRecommendedVideoTask');
            const ws = WebSocketClient.getInstance();
            const generateRecommendedVideoResponse: GenerateRecommendedVideoResponse =
                await ws.waitForMessage<GenerateRecommendedVideoResponse>(
                    generateRecommendedVideoTask.data.task_id,
                    TaskResultMessageTypeEnum.TASK_RESULT,
                    'generate_recommended_video',
                    {
                        partialResponseCallback: (partialResult) => {
                            call(
                                addOverlaysFromRecommendationsOperation,
                                partialResult,
                                sceneGroupId,
                                isGlobalVideo,
                                systemTriggered,
                                parentActionGroup,
                            );
                        },
                    },
                );

            await call(
                addOverlaysFromRecommendationsOperation,
                generateRecommendedVideoResponse,
                sceneGroupId,
                isGlobalVideo,
                systemTriggered,
                parentActionGroup,
            );
            dispatch(
                setRecommendedVideoResponse({
                    response: generateRecommendedVideoResponse,
                }),
            );
            dispatch(refreshRecommendedAssetsAfterLoadingRecommendedVideo());
        }

        if (
            optimizing &&
            isGlobalVideo &&
            Flags.canUseAgentFamily() &&
            !shouldSkipAgentPipeline(getState())
        ) {
            const {
                createBlueprintAgentOperation,
                createImproveMediaAgentOperation,
                createRecommendTextAssetTimingAgentOperation,
            } = await import('js/src/agent/agentOperations'); // dynamic import to avoid circular dependency
            const skipAutoImproveForExperiment =
                Flags.canUseSmartActionsTab() && Flags.disableGenxAutoImproveMedia();
            const useImproveMedia =
                Flags.canUseImproveMediaAgent() &&
                !skipAutoImproveForExperiment &&
                narrative_thread !== NarrativeThreadEnum.TALKING_HEAD;
            const useBlueprintAgent =
                Flags.canUseBlueprintAgent() &&
                narrative_thread !== NarrativeThreadEnum.TALKING_HEAD;
            const agentPipeline: AgentTaskPipeline = new AgentTaskPipeline([
                ...(useImproveMedia
                    ? [
                          createImproveMediaAgentOperation(narrative_thread, sceneGroup)(
                              luminary,
                              dispatch,
                              getState,
                          ),
                      ]
                    : []),
                ...(useBlueprintAgent
                    ? [createBlueprintAgentOperation()(luminary, dispatch, getState)]
                    : []),
                createRecommendTextAssetTimingAgentOperation(sceneGroup)(
                    luminary,
                    dispatch,
                    getState,
                ),
            ]);
            try {
                setAgentPipeline(agentPipeline);
                await agentPipeline.run();
            } catch (agentError) {
                agentPipeline.cancel();
                ErrorReporter.capture(agentError);
            } finally {
                setAgentPipeline(null);
            }
        }
    } catch (pollError: unknown) {
        if (isErrorObject(pollError)) {
            if (pollError.error instanceof OperationAbortedError) {
                // user canceled recommendations
                return;
            }
        }
        ErrorReporter.capture(pollError);
        Toast.error('Sorry, something went wrong while generating overlay recommendations.');
        analytics.track('Error generating overlays', {
            error: pollError,
            task_id: generateRecommendedVideoTask?.data?.task_id,
        });
    } finally {
        if (isGlobalVideo) {
            // clear the loading state
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));

            const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
            if (sceneGroup) {
                // select the first scene in the scene group
                call(selectOverlayOperation, sceneGroupId, sceneGroup.scenes[0].id);
            }

            dispatch(fetchRecommendedSearchTerms(parseScript(luminary)));
            // make an undo/redo checkpoint
            call(
                checkpointVideoAndTranscriptOperation,
                sceneGroupId,
                {
                    // we want to make sure that the transcript is marked as still being untouched by the user
                    isOriginalTranscript: true,
                },
                systemTriggered,
                parentActionGroup,
            );
        }
    }

    if (isGlobalVideo) {
        if (!Cookies.get(COOKIE_KEYS.FEEDBACK_VOICEOVER_AI_SHOWN)) {
            dispatch(
                setVideoFeedbackFormState({
                    isVisible: true,
                    source: 'Creator (Voiceover overlays)',
                }),
            );
            Cookies.set(COOKIE_KEYS.FEEDBACK_VOICEOVER_AI_SHOWN, 'true', {
                expires: 365,
            });
        } else if (selectedBlueprintId) {
            dispatch(
                setVideoFeedbackFormState({
                    isVisible: true,
                    source: `Creator (${narrative_thread} Blueprints)`,
                }),
            );
        }
    }
};

/**
 * This method is used to provide a guesstimate of the start and end time for each of the recommended scenes transcript words
 * so we can create a AIVO blueprint preview video without creating the actual AIVO.
 * This method is also used to get sentences from the recommended scenes.
 */
export const createBestGuessTranscript = (
    scene_recommendations: SceneRecommendation[],
): { transcript: AssetTranscript; sentences: string[]; audioAsset: ImmutableAudioAsset } => {
    const _get_random_word_time = () => {
        // pick a random time between 0.3 and 0.45 seconds
        return Math.random() * 0.15 + 0.3;
    };
    const _get_end_sentence_time = (word: string) => {
        // if word ends with sentence-ending punctuation return an extra 1s for a pause between sentences
        if (/[.!?]$/.test(word)) {
            return 1;
        }
        return 0;
    };

    let startTime = 0;
    const transcriptWords: TranscriptWord[] = [];
    const sentences: string[] = [];
    scene_recommendations.forEach((recommendation) => {
        sentences.push(recommendation.text_words.join(' '));
        recommendation.text_words.forEach((word, index) => {
            const endTime = startTime + _get_random_word_time() + _get_end_sentence_time(word);
            transcriptWords.push({
                word,
                startTime,
                endTime,
                filler: false, // AI VO does not have filler words
            });
            startTime = endTime;
        });
    });
    const transcript: AssetTranscript = {
        hasVoiceover: true,
        transcriptWords,
        language: scene_recommendations[0].scene_data.language as LanguageEnum,
    };

    PLACEHOLDER_VOICEOVER_MODEL_DATA.duration = transcriptWords[transcriptWords.length - 1].endTime;
    const audioAsset = ImmutableFromJS(PLACEHOLDER_VOICEOVER_MODEL_DATA);

    return { transcript, sentences, audioAsset };
};

export function createTextOnMediaPlaceholderScenes(
    luminary: Luminary,
    scriptSegments: string[],
    sceneDesignId: string,
    targetIndex: number,
): PlaceholderSceneGroupIdPerScriptSegment {
    const placeholderSceneGroupIdPerScriptSegment: PlaceholderSceneGroupIdPerScriptSegment = {};
    luminary.batchRun(() => {
        for (let segmentIndex = 0; segmentIndex < scriptSegments.length; segmentIndex++) {
            // create scene group
            const { sceneGroup, scene } = luminary.addSceneGroup({
                sceneDesignId,
            });
            placeholderSceneGroupIdPerScriptSegment[segmentIndex] = sceneGroup.id;

            // set text to first text asset and clear other content
            luminary.moveSceneGroupToIndex(sceneGroup.id, targetIndex + segmentIndex);
            getEditableMediaAssets(scene).forEach((mediaAsset) => clearSourceData(mediaAsset));
            getEditableSceneTextAssetArray(scene).forEach((fe, idx) => {
                if (idx === 0) {
                    fe.text = scriptSegments[segmentIndex];
                } else if (idx > 0) {
                    // clear default content from any other text element
                    fe.text = '';
                }
                fe.textHighlights = [];
                fe.setFontSizeToFitAllText();
            });
        }
    });
    return placeholderSceneGroupIdPerScriptSegment;
}

/**
 * Fetches a recommended music track given the luminary instance's script and sets it.
 */
export async function fetchAndSetRecommendMusic(
    luminary: Luminary,
    musicRecommendationMode: MusicRecommendationModeEnum = MusicRecommendationModeEnum.RECOMMENDED,
) {
    const script = parseScript(luminary);
    const videoDuration = Math.round(luminary.duration / 1000);
    try {
        const task_response = await ApiService.musicRecommendationCreate({
            body: {
                text: script,
                min_duration: videoDuration,
                music_recommendation_mode: musicRecommendationMode,
            },
        });
        const ws = WebSocketClient.getInstance();
        const music_response = await ws.waitForMessage<AudioAssetModel>(
            task_response.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'music_recommendation',
        );
        luminary.music = music_response as AudioSourceData;

        // ensure outro has no music if it has media with volume
        muteOutroMusicIfHasMediaWithVolume(luminary);
    } catch (e) {
        // don't show error to user
        ErrorReporter.capture(e);
    }
}

/**
 * Re-aligns recommendation text_words to match the actual transcript word tokens.
 *
 * The recommendation's text_words may come from a different tokenizer (e.g. our word segmentation)
 * than the TTS transcript (e.g. ElevenLabs). Both represent the same text but may split words
 * differently around punctuation and abbreviations (e.g. "RR.HH." + ":" vs "RR.HH.:").
 *
 * This function takes the transcript words (as they appear in the Lexical editor) and splits
 * them to align with each scene's text_words by matching joined character content, similar to
 * the backend's split_transcript_words_list_into_segment_lists.
 *
 * Returns the updated scenes array with corrected text_words, or null if alignment fails.
 */
/**
 * Normalizes a string for comparison by lowercasing, removing whitespace,
 * and applying Unicode NFC normalization. This matches the backend's approach
 * of ignoring whitespace differences during word alignment.
 */
function normalizeForComparison(text: string): string {
    return text.toLowerCase().replace(/\s/g, '').normalize('NFC');
}

export function realignRecommendationTextWords(
    scenes: (SceneRecommendation | null)[],
    transcriptWordStrings: string[],
): (SceneRecommendation | null)[] | null {
    const remainingWords = [...transcriptWordStrings];
    let failed = false;

    const updatedScenes = scenes.map((scene) => {
        if (failed || scene === null) {
            return scene;
        }

        const targetText = normalizeForComparison(scene.text_words.join(''));
        const alignedWords: string[] = [];
        let currentText = '';

        while (currentText !== targetText) {
            if (remainingWords.length === 0) {
                failed = true;
                return scene;
            }
            alignedWords.push(remainingWords.shift()!);
            currentText = normalizeForComparison(alignedWords.join(''));
            if (currentText.length > targetText.length) {
                failed = true;
                return scene;
            }
        }

        return {
            ...scene,
            text_words: alignedWords,
        };
    });

    if (failed) {
        return null;
    }

    return updatedScenes as GenerateRecommendedVideoResponse['scenes'];
}
